/**
 * 운동 썸네일 CDN 해석 — 앱 exerciseThumbnailAssets.ts 를 웹용으로 미러링.
 *
 * 앱은 운동 이미지 300장을 S3(rappo-image 버킷)에 올려 CloudFront 로 서빙한다.
 * 웹/백엔드/앱 모두 동일한 CloudFront URL 을 참조한다. 이 CloudFront 는 dev·prod 공용이라
 * 환경별 스왑이 필요 없다.
 *   URL = EXERCISE_THUMBNAIL_BASE + '<key>.jpg'
 *   key = exerciseId 에서 'default-' 접두어 제거, 또는 한글 운동명 매핑
 *
 * ⚠️ 앱이 이미지를 추가/변경하면(exerciseId/이름 매핑) 이 파일 재생성 + 신규 파일 S3 업로드.
 *
 * 총 300개 key, 이름 매핑 289개.
 */

// 운동 이미지 CloudFront(rappo-image 오리진, dev·prod 공용). env 로 override 가능. 뒤에 '/' 1개로 정규화.
const RAW_THUMBNAIL_BASE =
    process.env.REACT_APP_EXERCISE_THUMBNAIL_BASE ||
    'https://d1oioiexdn2j0u.cloudfront.net/exercise-thumbnails';
export const EXERCISE_THUMBNAIL_BASE = `${RAW_THUMBNAIL_BASE.replace(/\/+$/, '')}/`;

/** 업로드된 썸네일 key 집합 (파일명 stem) */
const EXERCISE_THUMBNAIL_KEYS = new Set<string>([
    'ab-crunch-machine',
    'ab-wheel-rollout',
    'air-bike',
    'arc-trainer',
    'arm-bike',
    'arnold-press',
    'assisted-dip',
    'assisted-pull-up',
    'assisted-push-up-machine',
    'back-extension',
    'band-assisted-pull-up',
    'band-pull-apart',
    'barbell-bench-press',
    'barbell-curl',
    'barbell-floor-press',
    'barbell-good-morning',
    'barbell-hip-thrust',
    'barbell-lunge',
    'barbell-row',
    'barbell-squat',
    'battle-rope',
    'bear-crawl',
    'behind-neck-press',
    'belt-squat',
    'bench-dip',
    'bent-over-lateral-raise',
    'bicycle-crunch',
    'bike',
    'bird-dog',
    'bodyweight-squat',
    'box-jump',
    'brisk-walk',
    'bulgarian-split-squat',
    'burpee',
    'cable-arm-pulldown',
    'cable-close-row',
    'cable-crossover',
    'cable-crunch',
    'cable-curl',
    'cable-decline-fly',
    'cable-external-rotation',
    'cable-front-raise',
    'cable-glute-kickback',
    'cable-high-fly',
    'cable-hip-abduction',
    'cable-hip-adduction',
    'cable-incline-fly',
    'cable-lateral-raise',
    'cable-low-fly',
    'cable-preacher-curl',
    'cable-pull-through',
    'cable-pullover',
    'cable-rear-delt-fly',
    'cable-reverse-lunge',
    'cable-rope-curl',
    'cable-rope-overhead-extension',
    'cable-single-arm-chest-press',
    'cable-upright-row',
    'cable-wide-row',
    'cable-woodchop',
    'calf-raise',
    'captain-chair-leg-raise',
    'chest-press',
    'chest-supported-row',
    'chest-supported-t-bar-row',
    'chin-up',
    'close-grip-bench-press',
    'close-grip-lat-pulldown',
    'close-grip-push-up',
    'concentration-curl',
    'cross-body-hammer-curl',
    'crunch',
    'cuban-press',
    'curtsy-lunge',
    'dead-bug',
    'deadlift',
    'decline-bench-press',
    'decline-chest-press-machine',
    'decline-dumbbell-fly',
    'decline-sit-up',
    'deficit-deadlift',
    'diamond-push-up',
    'dip',
    'dip-machine',
    'donkey-calf-raise',
    'double-under',
    'drag-curl',
    'dumbbell-bench-press',
    'dumbbell-curl',
    'dumbbell-floor-press',
    'dumbbell-fly',
    'dumbbell-pullover',
    'dumbbell-rear-delt-raise',
    'dumbbell-romanian-deadlift',
    'dumbbell-row',
    'dumbbell-shoulder-press',
    'dumbbell-side-bend',
    'dumbbell-squeeze-press',
    'dumbbell-step-up',
    'dumbbell-upright-row',
    'dumbbell-walking-lunge',
    'elliptical',
    'ez-bar-curl',
    'face-pull',
    'farmer-walk',
    'foam-roller',
    'front-raise',
    'front-squat',
    'glute-bridge',
    'glute-ham-raise',
    'glute-kickback-machine',
    'goblet-squat',
    'hack-squat',
    'hammer-curl',
    'hammer-strength-chest-press',
    'hammer-strength-row',
    'hamstring-stretch',
    'hanging-knee-raise',
    'hanging-leg-raise',
    'high-cable-curl',
    'high-knees',
    'high-row-machine',
    'hip-abduction',
    'hip-adduction',
    'hip-stretch',
    'hip-thrust',
    'hollow-body-hold',
    'inchworm',
    'incline-bench-dumbbell-row',
    'incline-bench-press',
    'incline-chest-press-machine',
    'incline-crunch',
    'incline-dumbbell-bench-press',
    'incline-dumbbell-curl',
    'incline-dumbbell-fly',
    'incline-treadmill-run',
    'incline-treadmill-walk',
    'incline-y-raise',
    'interval-running',
    'inverted-row',
    'iso-lateral-row',
    'jump-rope',
    'jumping-jack',
    'kettlebell-swing',
    'kneeling-single-arm-lat-pulldown',
    'landmine-chest-press',
    'landmine-press',
    'landmine-rotation',
    'landmine-row',
    'lat-pulldown',
    'lateral-lunge',
    'lateral-raise',
    'lateral-shuffle',
    'leaning-cable-lateral-raise',
    'leg-curl',
    'leg-extension',
    'leg-press',
    'leg-raise',
    'low-row-machine',
    'lunge',
    'lying-dumbbell-triceps-extension',
    'lying-leg-curl-machine',
    'machine-biceps-curl',
    'machine-chest-fly',
    'machine-lateral-raise',
    'machine-preacher-curl',
    'machine-pullover',
    'machine-rear-delt-fly',
    'machine-triceps-extension',
    'manual-treadmill',
    'meadows-row',
    'mountain-climber',
    'narrow-stance-leg-press',
    'neutral-assisted-pull-up',
    'neutral-grip-lat-pulldown',
    'nordic-hamstring-curl',
    'overhead-squat',
    'overhead-triceps-extension',
    'pallof-press',
    'pec-deck-fly',
    'pendulum-squat',
    'plank',
    'plank-jack',
    'plank-shoulder-tap',
    'plate-front-raise',
    'plate-loaded-lat-pulldown',
    'plate-loaded-shoulder-press',
    'plate-press',
    'preacher-curl',
    'pull-up',
    'push-press',
    'push-up',
    'rack-pull',
    'rear-delt-fly',
    'recumbent-bike',
    'reverse-cable-woodchop',
    'reverse-curl',
    'reverse-grip-bench-press',
    'reverse-grip-lat-pulldown',
    'reverse-grip-triceps-pushdown',
    'reverse-hack-squat',
    'reverse-hyperextension',
    'reverse-lunge',
    'reverse-pec-deck-fly',
    'reverse-wrist-curl',
    'romanian-deadlift',
    'rope-climb',
    'rope-face-pull',
    'rope-hammer-curl',
    'rope-triceps-pushdown',
    'rotary-torso-machine',
    'rowing-machine',
    'running',
    'russian-twist',
    'scapular-pull-up',
    'seated-calf-raise-machine',
    'seated-dumbbell-triceps-extension',
    'seated-elliptical',
    'seated-good-morning',
    'seated-leg-curl',
    'seated-machine-row-wide',
    'seated-row',
    'shadow-boxing',
    'shoulder-press',
    'shrug',
    'shuttle-run',
    'side-hop',
    'side-plank',
    'single-arm-cable-curl',
    'single-arm-cable-row',
    'single-arm-cable-triceps-extension',
    'single-arm-chest-press-machine',
    'single-arm-lat-pulldown',
    'single-arm-machine-shoulder-press',
    'single-leg-extension',
    'single-leg-leg-press',
    'sissy-squat-machine',
    'skater-jump',
    'ski-erg',
    'skull-crusher',
    'slam-ball',
    'sled-push',
    'smith-machine-bench-press',
    'smith-machine-calf-raise',
    'smith-machine-close-grip-bench-press',
    'smith-machine-decline-bench-press',
    'smith-machine-front-squat',
    'smith-machine-good-morning',
    'smith-machine-hip-thrust',
    'smith-machine-incline-bench-press',
    'smith-machine-lunge',
    'smith-machine-romanian-deadlift',
    'smith-machine-shoulder-press',
    'smith-machine-squat',
    'spider-curl',
    'spin-bike',
    'split-jump',
    'squat-jump',
    'squat-thrust',
    'stair-climber',
    'stair-climbing',
    'stair-run',
    'standing-cable-row',
    'standing-calf-raise-machine',
    'standing-leg-curl-machine',
    'step-box',
    'step-up',
    'stepmill',
    'stepper',
    'stiff-leg-deadlift',
    'straight-arm-band-pulldown',
    'straight-bar-triceps-pushdown',
    'stretching',
    'sumo-deadlift',
    'swiss-ball-crunch',
    'swiss-ball-rollout',
    't-bar-row',
    'thoracic-rotation',
    'tibialis-raise',
    'toes-to-bar',
    'trap-bar-deadlift',
    'treadmill-run',
    'treadmill-walk',
    'triceps-kickback',
    'triceps-pushdown',
    'tuck-jump',
    'upright-row',
    'v-squat-machine',
    'v-up',
    'vertical-climber',
    'walking-lunge',
    'wall-slide',
    'weighted-crunch',
    'weighted-plank',
    'wide-grip-lat-pulldown',
    'wide-stance-leg-press',
    'wrist-curl',
    'z-press',
    'zercher-squat',
    'zottman-curl',
]);

/** 한글 운동명 → key (exerciseId 없거나 매칭 실패 시 fallback) */
const EXERCISE_NAME_TO_KEY: Record<string, string> = {
    '복근 크런치 머신': 'ab-crunch-machine',
    '앱 휠 롤아웃': 'ab-wheel-rollout',
    '아크 트레이너': 'arc-trainer',
    '암 바이크': 'arm-bike',
    '아놀드 프레스': 'arnold-press',
    '어시스트 딥': 'assisted-dip',
    '어시스트 풀업': 'assisted-pull-up',
    '어시스트 푸시업 머신': 'assisted-push-up-machine',
    '백 익스텐션': 'back-extension',
    '밴드 어시스트 풀업': 'band-assisted-pull-up',
    '밴드 풀어파트': 'band-pull-apart',
    '바벨 벤치프레스': 'barbell-bench-press',
    '바벨 컬': 'barbell-curl',
    '바벨 플로어 프레스': 'barbell-floor-press',
    '바벨 굿모닝': 'barbell-good-morning',
    '바벨 힙 쓰러스트': 'barbell-hip-thrust',
    '바벨 런지': 'barbell-lunge',
    '바벨 로우': 'barbell-row',
    '바벨 스쿼트': 'barbell-squat',
    '배틀로프': 'battle-rope',
    '베어 크롤': 'bear-crawl',
    '비하인드 넥 프레스': 'behind-neck-press',
    '벨트 스쿼트': 'belt-squat',
    '벤치 딥스': 'bench-dip',
    '벤트오버 레터럴 레이즈': 'bent-over-lateral-raise',
    '바이시클 크런치': 'bicycle-crunch',
    '사이클': 'bike',
    '버드독': 'bird-dog',
    '맨몸 스쿼트': 'bodyweight-squat',
    '박스 점프': 'box-jump',
    '빠르게 걷기': 'brisk-walk',
    '불가리안 스플릿 스쿼트': 'bulgarian-split-squat',
    '버피': 'burpee',
    '케이블 암풀다운': 'cable-arm-pulldown',
    '클로즈그립 케이블 로우': 'cable-close-row',
    '케이블 크로스오버': 'cable-crossover',
    '케이블 크런치': 'cable-crunch',
    '케이블 컬': 'cable-curl',
    '디클라인 케이블 플라이': 'cable-decline-fly',
    '케이블 외회전': 'cable-external-rotation',
    '케이블 프론트 레이즈': 'cable-front-raise',
    '케이블 글루트 킥백': 'cable-glute-kickback',
    '하이 케이블 플라이': 'cable-high-fly',
    '케이블 힙 어브덕션': 'cable-hip-abduction',
    '케이블 힙 어덕션': 'cable-hip-adduction',
    '인클라인 케이블 플라이': 'cable-incline-fly',
    '케이블 레터럴 레이즈': 'cable-lateral-raise',
    '로우 케이블 플라이': 'cable-low-fly',
    '케이블 프리처 컬': 'cable-preacher-curl',
    '케이블 풀스루': 'cable-pull-through',
    '케이블 풀오버': 'cable-pullover',
    '케이블 리어 델트 플라이': 'cable-rear-delt-fly',
    '케이블 리버스 런지': 'cable-reverse-lunge',
    '케이블 로프 컬': 'cable-rope-curl',
    '케이블 로프 오버헤드 익스텐션': 'cable-rope-overhead-extension',
    '원암 케이블 체스트 프레스': 'cable-single-arm-chest-press',
    '케이블 업라이트 로우': 'cable-upright-row',
    '와이드 케이블 로우': 'cable-wide-row',
    '케이블 우드찹': 'cable-woodchop',
    '카프 레이즈': 'calf-raise',
    '캡틴체어 레그 레이즈': 'captain-chair-leg-raise',
    '체스트 프레스': 'chest-press',
    '체스트 서포티드 로우': 'chest-supported-row',
    '체스트 서포티드 티바 로우': 'chest-supported-t-bar-row',
    '클로즈그립 벤치프레스': 'close-grip-bench-press',
    '클로즈그립 랫 풀다운': 'close-grip-lat-pulldown',
    '클로즈그립 푸시업': 'close-grip-push-up',
    '컨센트레이션 컬': 'concentration-curl',
    '크로스바디 해머 컬': 'cross-body-hammer-curl',
    '크런치': 'crunch',
    '쿠반 프레스': 'cuban-press',
    '커티시 런지': 'curtsy-lunge',
    '데드버그': 'dead-bug',
    '데드리프트': 'deadlift',
    '디클라인 벤치프레스': 'decline-bench-press',
    '디클라인 체스트 프레스 머신': 'decline-chest-press-machine',
    '디클라인 덤벨 플라이': 'decline-dumbbell-fly',
    '디클라인 싯업': 'decline-sit-up',
    '디피싯 데드리프트': 'deficit-deadlift',
    '다이아몬드 푸시업': 'diamond-push-up',
    '딥스': 'dip',
    '딥 머신': 'dip-machine',
    '동키 카프 레이즈': 'donkey-calf-raise',
    '더블 언더': 'double-under',
    '드래그 컬': 'drag-curl',
    '덤벨 벤치프레스': 'dumbbell-bench-press',
    '덤벨 컬': 'dumbbell-curl',
    '덤벨 플로어 프레스': 'dumbbell-floor-press',
    '덤벨 플라이': 'dumbbell-fly',
    '덤벨 풀오버': 'dumbbell-pullover',
    '덤벨 리어 델트 레이즈': 'dumbbell-rear-delt-raise',
    '덤벨 루마니안 데드리프트': 'dumbbell-romanian-deadlift',
    '덤벨 로우': 'dumbbell-row',
    '덤벨 숄더 프레스': 'dumbbell-shoulder-press',
    '덤벨 사이드 밴드': 'dumbbell-side-bend',
    '덤벨 스퀴즈 프레스': 'dumbbell-squeeze-press',
    '덤벨 스텝업': 'dumbbell-step-up',
    '덤벨 업라이트 로우': 'dumbbell-upright-row',
    '덤벨 워킹 런지': 'dumbbell-walking-lunge',
    '일립티컬': 'elliptical',
    '이지바 컬': 'ez-bar-curl',
    '페이스 풀': 'face-pull',
    '파머스 워크': 'farmer-walk',
    '폼롤러': 'foam-roller',
    '프론트 레이즈': 'front-raise',
    '프론트 스쿼트': 'front-squat',
    '글루트 브릿지': 'glute-bridge',
    '글루트 햄 레이즈': 'glute-ham-raise',
    '글루트 킥백 머신': 'glute-kickback-machine',
    '고블릿 스쿼트': 'goblet-squat',
    '핵 스쿼트': 'hack-squat',
    '해머 컬': 'hammer-curl',
    '해머 체스트 프레스': 'hammer-strength-chest-press',
    '해머 로우': 'hammer-strength-row',
    '햄스트링 스트레칭': 'hamstring-stretch',
    '행잉 니 레이즈': 'hanging-knee-raise',
    '행잉 레그 레이즈': 'hanging-leg-raise',
    '하이 케이블 컬': 'high-cable-curl',
    '하이 니': 'high-knees',
    '하이 로우 머신': 'high-row-machine',
    '힙 어브덕션': 'hip-abduction',
    '힙 어덕션': 'hip-adduction',
    '고관절 스트레칭': 'hip-stretch',
    '힙 쓰러스트': 'hip-thrust',
    '할로우 바디 홀드': 'hollow-body-hold',
    '인클라인 벤치 덤벨 로우': 'incline-bench-dumbbell-row',
    '인클라인 벤치프레스': 'incline-bench-press',
    '인클라인 체스트 프레스 머신': 'incline-chest-press-machine',
    '인클라인 크런치': 'incline-crunch',
    '인클라인 덤벨 벤치프레스': 'incline-dumbbell-bench-press',
    '인클라인 덤벨 컬': 'incline-dumbbell-curl',
    '인클라인 덤벨 플라이': 'incline-dumbbell-fly',
    '경사 트레드밀 러닝': 'incline-treadmill-run',
    '경사 트레드밀 걷기': 'incline-treadmill-walk',
    '인클라인 Y 레이즈': 'incline-y-raise',
    '인터벌 러닝': 'interval-running',
    '인버티드 로우': 'inverted-row',
    '아이소 레터럴 로우': 'iso-lateral-row',
    '점핑 잭': 'jumping-jack',
    '케틀벨 스윙': 'kettlebell-swing',
    '니링 원암 랫 풀다운': 'kneeling-single-arm-lat-pulldown',
    '랜드마인 체스트 프레스': 'landmine-chest-press',
    '랜드마인 프레스': 'landmine-press',
    '랜드마인 로테이션': 'landmine-rotation',
    '랜드마인 로우': 'landmine-row',
    '랫 풀다운': 'lat-pulldown',
    '사이드 런지': 'lateral-lunge',
    '레터럴 레이즈': 'lateral-raise',
    '사이드 셔플': 'lateral-shuffle',
    '린닝 케이블 레터럴 레이즈': 'leaning-cable-lateral-raise',
    '레그 컬': 'leg-curl',
    '레그 익스텐션': 'leg-extension',
    '레그 프레스': 'leg-press',
    '레그 레이즈': 'leg-raise',
    '로우 로우 머신': 'low-row-machine',
    '런지': 'lunge',
    '라잉 덤벨 트라이셉스 익스텐션': 'lying-dumbbell-triceps-extension',
    '라잉 레그컬 머신': 'lying-leg-curl-machine',
    '머신 바이셉스 컬': 'machine-biceps-curl',
    '머신 체스트 플라이': 'machine-chest-fly',
    '머신 레터럴 레이즈': 'machine-lateral-raise',
    '머신 프리처 컬': 'machine-preacher-curl',
    '머신 풀오버': 'machine-pullover',
    '머신 리어 델트 플라이': 'machine-rear-delt-fly',
    '머신 트라이셉스 익스텐션': 'machine-triceps-extension',
    '무동력 트레드밀': 'manual-treadmill',
    '메도우스 로우': 'meadows-row',
    '마운틴 클라이머': 'mountain-climber',
    '내로우 스탠스 레그 프레스': 'narrow-stance-leg-press',
    '뉴트럴 어시스트 풀업': 'neutral-assisted-pull-up',
    '뉴트럴그립 랫 풀다운': 'neutral-grip-lat-pulldown',
    '노르딕 햄스트링 컬': 'nordic-hamstring-curl',
    '오버헤드 스쿼트': 'overhead-squat',
    '오버헤드 트라이셉스 익스텐션': 'overhead-triceps-extension',
    '팔로프 프레스': 'pallof-press',
    '펙덱 플라이': 'pec-deck-fly',
    '펜듈럼 스쿼트': 'pendulum-squat',
    '플랭크': 'plank',
    '플랭크 잭': 'plank-jack',
    '플랭크 숄더 탭': 'plank-shoulder-tap',
    '플레이트 프론트 레이즈': 'plate-front-raise',
    '플레이트 로드 랫 풀다운': 'plate-loaded-lat-pulldown',
    '플레이트 로드 숄더 프레스': 'plate-loaded-shoulder-press',
    '플레이트 프레스': 'plate-press',
    '프리처 컬': 'preacher-curl',
    '풀 업': 'pull-up',
    '푸시 프레스': 'push-press',
    '푸시업': 'push-up',
    '랙 풀': 'rack-pull',
    '리어 델트 플라이': 'rear-delt-fly',
    '좌식 사이클': 'recumbent-bike',
    '리버스 케이블 우드찹': 'reverse-cable-woodchop',
    '리버스 컬': 'reverse-curl',
    '리버스그립 벤치프레스': 'reverse-grip-bench-press',
    '리버스그립 랫 풀다운': 'reverse-grip-lat-pulldown',
    '리버스그립 트라이셉스 푸시다운': 'reverse-grip-triceps-pushdown',
    '리버스 핵 스쿼트': 'reverse-hack-squat',
    '리버스 하이퍼익스텐션': 'reverse-hyperextension',
    '리버스 런지': 'reverse-lunge',
    '리버스 펙덱 플라이': 'reverse-pec-deck-fly',
    '리버스 리스트 컬': 'reverse-wrist-curl',
    '루마니안 데드리프트': 'romanian-deadlift',
    '로프 클라임': 'rope-climb',
    '로프 페이스 풀': 'rope-face-pull',
    '로프 해머 컬': 'rope-hammer-curl',
    '로프 트라이셉스 푸시다운': 'rope-triceps-pushdown',
    '로터리 토르소 머신': 'rotary-torso-machine',
    '로잉머신': 'rowing-machine',
    '러시안 트위스트': 'russian-twist',
    '스캐풀라 풀업': 'scapular-pull-up',
    '시티드 카프 레이즈 머신': 'seated-calf-raise-machine',
    '시티드 덤벨 트라이셉스 익스텐션': 'seated-dumbbell-triceps-extension',
    '좌식 일립티컬': 'seated-elliptical',
    '시티드 굿모닝': 'seated-good-morning',
    '시티드 레그컬': 'seated-leg-curl',
    '와이드 시티드 로우 머신': 'seated-machine-row-wide',
    '시티드 로우': 'seated-row',
    '쉐도우 복싱': 'shadow-boxing',
    '숄더 프레스': 'shoulder-press',
    '왕복 달리기': 'shuttle-run',
    '좌우 점프': 'side-hop',
    '사이드 플랭크': 'side-plank',
    '원암 케이블 컬': 'single-arm-cable-curl',
    '원암 케이블 로우': 'single-arm-cable-row',
    '원암 케이블 트라이셉스 익스텐션': 'single-arm-cable-triceps-extension',
    '원암 체스트 프레스 머신': 'single-arm-chest-press-machine',
    '원암 랫 풀다운': 'single-arm-lat-pulldown',
    '원암 머신 숄더 프레스': 'single-arm-machine-shoulder-press',
    '싱글 레그 익스텐션': 'single-leg-extension',
    '싱글 레그 프레스': 'single-leg-leg-press',
    '시시 스쿼트 머신': 'sissy-squat-machine',
    '스케이터 점프': 'skater-jump',
    '스컬 크러셔': 'skull-crusher',
    '슬램볼': 'slam-ball',
    '썰매 밀기': 'sled-push',
    '스미스머신 벤치프레스': 'smith-machine-bench-press',
    '스미스머신 카프 레이즈': 'smith-machine-calf-raise',
    '스미스머신 클로즈그립 벤치프레스': 'smith-machine-close-grip-bench-press',
    '스미스머신 디클라인 벤치프레스': 'smith-machine-decline-bench-press',
    '스미스머신 프론트 스쿼트': 'smith-machine-front-squat',
    '스미스머신 굿모닝': 'smith-machine-good-morning',
    '스미스머신 힙 쓰러스트': 'smith-machine-hip-thrust',
    '스미스머신 인클라인 벤치프레스': 'smith-machine-incline-bench-press',
    '스미스머신 런지': 'smith-machine-lunge',
    '스미스머신 루마니안 데드리프트': 'smith-machine-romanian-deadlift',
    '스미스머신 숄더 프레스': 'smith-machine-shoulder-press',
    '스미스머신 스쿼트': 'smith-machine-squat',
    '스파이더 컬': 'spider-curl',
    '스핀 바이크': 'spin-bike',
    '스플릿 점프': 'split-jump',
    '스쿼트 점프': 'squat-jump',
    '스쿼트 쓰러스트': 'squat-thrust',
    '천국의 계단': 'stair-climber',
    '계단 오르기': 'stair-climbing',
    '계단 달리기': 'stair-run',
    '스탠딩 케이블 로우': 'standing-cable-row',
    '스탠딩 카프 레이즈 머신': 'standing-calf-raise-machine',
    '스탠딩 레그컬 머신': 'standing-leg-curl-machine',
    '스텝업': 'step-up',
    '스티프 레그 데드리프트': 'stiff-leg-deadlift',
    '밴드 스트레이트 암 풀다운': 'straight-arm-band-pulldown',
    '스트레이트바 트라이셉스 푸시다운': 'straight-bar-triceps-pushdown',
    '스트레칭': 'stretching',
    '스모 데드리프트': 'sumo-deadlift',
    '짐볼 크런치': 'swiss-ball-crunch',
    '짐볼 롤아웃': 'swiss-ball-rollout',
    '티바 로우': 't-bar-row',
    '흉추 회전': 'thoracic-rotation',
    '티비얼리스 레이즈': 'tibialis-raise',
    '토즈 투 바': 'toes-to-bar',
    '트랩바 데드리프트': 'trap-bar-deadlift',
    '트레드밀 러닝': 'treadmill-run',
    '트레드밀 걷기': 'treadmill-walk',
    '트라이셉스 킥백': 'triceps-kickback',
    '트라이셉스 푸시다운': 'triceps-pushdown',
    '턱 점프': 'tuck-jump',
    '업라이트 로우': 'upright-row',
    '브이스쿼트 머신': 'v-squat-machine',
    '브이업': 'v-up',
    '워킹 런지': 'walking-lunge',
    '월슬라이드': 'wall-slide',
    '웨이티드 크런치': 'weighted-crunch',
    '웨이티드 플랭크': 'weighted-plank',
    '와이드그립 랫 풀다운': 'wide-grip-lat-pulldown',
    '와이드 스탠스 레그 프레스': 'wide-stance-leg-press',
    '리스트 컬': 'wrist-curl',
    '제트 프레스': 'z-press',
    '저처 스쿼트': 'zercher-squat',
    '조트맨 컬': 'zottman-curl',
};

const normalizeKey = (value?: string): string | undefined =>
    value ? value.replace(/^default-/, '').trim() : undefined;

/**
 * 운동 CDN 썸네일 URL 해석. exerciseId 우선, 없으면 한글 운동명 매핑. 없으면 undefined.
 */
export const getExerciseThumbnailUrl = (
    exerciseId?: string,
    exerciseName?: string,
): string | undefined => {
    const idKey = normalizeKey(exerciseId);
    if (idKey && EXERCISE_THUMBNAIL_KEYS.has(idKey)) {
        return `${EXERCISE_THUMBNAIL_BASE}${idKey}.jpg`;
    }
    const nameKey = exerciseName ? EXERCISE_NAME_TO_KEY[exerciseName] : undefined;
    if (nameKey && EXERCISE_THUMBNAIL_KEYS.has(nameKey)) {
        return `${EXERCISE_THUMBNAIL_BASE}${nameKey}.jpg`;
    }
    return undefined;
};
